import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, Mail, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';

  const content = {
    title: isTurkish ? 'Gizlilik Politikası' : 'Privacy Policy',
    lastUpdated: isTurkish ? 'Son güncelleme: Ocak 2026' : 'Last updated: January 2026',
    intro: isTurkish 
      ? 'PIRONE olarak gizliliğinizi ciddiye alıyoruz. Bu Gizlilik Politikası, web sitemizi ziyaret ettiğinizde bilgilerinizi nasıl topladığımızı, kullandığımızı, ifşa ettiğimizi ve koruduğumuzu açıklar.'
      : 'At PIRONE, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.',
    sections: [
      {
        icon: FileText,
        title: isTurkish ? 'Topladığımız Bilgiler' : 'Information We Collect',
        items: isTurkish 
          ? ['İsim ve iletişim bilgileri', 'E-posta adresi', 'Şirket bilgileri', 'Proje gereksinimleri']
          : ['Name and contact information', 'Email address', 'Company information', 'Project requirements']
      },
      {
        icon: Eye,
        title: isTurkish ? 'Bilgilerinizi Nasıl Kullanıyoruz' : 'How We Use Your Information',
        items: isTurkish
          ? ['Hizmet taleplerinizi işlemek', 'Güncellemeler ve bildirimler göndermek', 'Hizmetlerimizi geliştirmek', 'Müşteri desteği sağlamak']
          : ['Process your service requests', 'Send updates and notifications', 'Improve our services', 'Provide customer support']
      },
      {
        icon: Shield,
        title: isTurkish ? 'Veri Güvenliği' : 'Data Security',
        items: isTurkish
          ? ['SSL şifreleme kullanıyoruz', 'Veriler güvenli sunucularda saklanır', 'Düzenli güvenlik denetimleri yapılır', 'Yetkisiz erişime karşı koruma']
          : ['We use SSL encryption', 'Data is stored on secure servers', 'Regular security audits', 'Protection against unauthorized access']
      },
      {
        icon: Lock,
        title: isTurkish ? 'Haklarınız' : 'Your Rights',
        items: isTurkish
          ? ['Verilerinize erişim hakkı', 'Düzeltme talep etme hakkı', 'Silme talep etme hakkı', 'İşlemeyi reddetme hakkı']
          : ['Right to access your data', 'Right to request correction', 'Right to request deletion', 'Right to object to processing']
      }
    ],
    contact: isTurkish 
      ? 'Gizlilik politikamız hakkında sorularınız varsa, lütfen bizimle iletişime geçin:'
      : 'If you have questions about our privacy policy, please contact us:'
  };

  return (
    <div className="min-h-screen bg-transparent py-16 relative z-10">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center mx-auto mb-6">
            <Shield size={32} className="text-[#38BDF8]" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{content.title}</h1>
          <p className="text-white/40 flex items-center justify-center gap-2">
            <Calendar size={16} />
            {content.lastUpdated}
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] mb-8"
        >
          <p className="text-white/70 leading-relaxed">{content.intro}</p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {content.sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
              className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center">
                  <section.icon size={20} className="text-[#38BDF8]" />
                </div>
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
              </div>
              <ul className="space-y-2 ml-14">
                {section.items.map((item, i) => (
                  <li key={i} className="text-white/60 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#38BDF8]/50" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mt-12 p-6 rounded-2xl bg-[#38BDF8]/5 border border-[#38BDF8]/20 text-center"
        >
          <p className="text-white/60 mb-4">{content.contact}</p>
          <a 
            href="mailto:info@pirone.io" 
            className="inline-flex items-center gap-2 text-[#38BDF8] hover:underline"
          >
            <Mail size={18} />
            info@pirone.io
          </a>
        </motion.div>

        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-8 text-center"
        >
          <Link 
            to="/" 
            className="text-white/40 hover:text-white transition-colors"
          >
            ← {isTurkish ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
